// @flow

import { toastr } from 'react-redux-toastr';
import { makeApiCall } from '../../../../services/apiService/apiService';
import {
  downloadArrayBufferAsXLS,
  checkForDominoPrintechClient,
  checkForClientUser,
  downloadPdf,
  clientAppUuid,
  jsonToCSVConvertor
} from '../../../../services/helpers';

export const createBulkCnActions = {
  CREATE_BULK_CN: 'CREATE_BULK_CN',
  ADD_USER_TYPE: 'ADD_USER_TYPE',
  ADD_ORGANISATION_DETAILS_LIST: 'ADD_ORGANISATION_DETAILS_LIST',
  ADD_ORGANISATION_CODE_FOR_BF_USER: 'ADD_ORGANISATION_CODE_FOR_BF_USER',
  ADD_TEMPLATE_LIST_USING_ORGANISATION_CODE:
    'ADD_TEMPLATE_LIST_USING_ORGANISATION_CODE',
  ADD_LOCATION_LIST_USING_ORGANISATION_ID:
    'ADD_LOCATION_LIST_USING_ORGANISATION_ID'
};

export const dispatchPayload = (obj: Object) => ({
  type: createBulkCnActions.CREATE_BULK_CN,
  payload: obj
});

/**
 * dispatchUserDetails: Method to be dispatched on api call.
 * To add user type to store.
 *
 * @param payload
 * @returns {{payload: *, type: string}}
 */
export const dispatchUserDetails = payload => ({
  type: createBulkCnActions.ADD_USER_TYPE,
  payload
});

/**
 * dispatchOrganisationDetailsList: Method to be dispatched on api call.
 * To add organisation details list to store.
 *
 * @param payload
 * @returns {{payload: *, type: string}}
 */
export const dispatchOrganisationDetailsList = payload => ({
  type: createBulkCnActions.ADD_ORGANISATION_DETAILS_LIST,
  payload
});

/**
 * dispatchOrganisationCodeForBfUser: Method to be dispatched on api call.
 * To add organisation code for BF user.
 *
 * @param payload
 * @returns {{payload: *, type: string}}
 */
export const dispatchOrganisationCodeForBfUser = payload => ({
  type: createBulkCnActions.ADD_ORGANISATION_CODE_FOR_BF_USER,
  payload
});

/**
 * dispatchTemplateListUsingOrganisationCode: Method to be dispatched on api call.
 * To add template list using organisation code.
 *
 * @param payload
 * @returns {{payload: *, type: string}}
 */
export const dispatchTemplateListUsingOrganisationCode = payload => ({
  type: createBulkCnActions.ADD_TEMPLATE_LIST_USING_ORGANISATION_CODE,
  payload
});

/**
 * dispatcLocationListUsingOrganisationId: Method to be dispatched on api call.
 * To add location list using organisation id
 *
 * @param {*} payload
 * @returns
 */
export const dispatcLocationListUsingOrganisationId = payload => ({
  type: createBulkCnActions.ADD_LOCATION_LIST_USING_ORGANISATION_ID,
  payload
});

const headersData = { appUuid: clientAppUuid };

export const createBulkCn = (
  file: File,
  organisationCode: String,
  clientCode: String,
  locationCode: String,
  templateId: String,
  isCnCompletionCall: Boolean,
  cb: Function
) => (dispatch: Function, getState) => {
  if (!file) {
    dispatch(
      dispatchPayload({
        isLoading: false,
        isLoaded: true
      })
    );
    return;
  }

  dispatch(
    dispatchPayload({
      isLoading: true,
      isLoaded: false
    })
  );
  const formData = new FormData();
  formData.append('file', file);
  formData.append('organisationCode', organisationCode);
  formData.append('clientCode', clientCode);
  formData.append('bfTerminalLocation', locationCode);
  formData.append('formatId', templateId);
  formData.append('isCnCompletionCall', isCnCompletionCall);

  const requestObj = {
    headersData: { appUuid: headersData.appUuid },
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'bulkUploadCn',
    postFormData: true,
    data: formData,
    contentType: undefined,
    method: 'POST',
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(
          dispatchPayload({
            isLoading: false,
            isLoaded: true
          })
        );
        toastr.success('Success', `Successfully uploaded file.`);
        cb(
          `Successfully uploaded file. Please check the status with requestId ${res.payload.requestId}`
        );
      } else {
        dispatch(
          dispatchPayload({
            isLoading: false,
            isLoaded: true
          })
        );
        toastr.error(
          'Error',
          res.errorMessage || 'Failed to upload file. Please try again.'
        );
      }
    },
    failureCb: res => {
      toastr.error(
        'Error',
        res.errorMessage || 'Failed to upload file. Please try again.'
      );
      dispatch(
        dispatchPayload({
          isLoading: false,
          isLoaded: true
        })
      );
    },
    catchCb: res => {
      dispatch(
        dispatchPayload({
          isLoading: false,
          isLoaded: true
        })
      );
    }
  };

  return makeApiCall(requestObj);
};

export const getBarcodesString = (obj: Object, userProfile: Object, cb) => (
  dispatch: Function
) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };
  const requestObj = {
    baseUrl:
      obj.type === 'cnoteList' ? 'zoomApiUrl' : 'clientIntegrationApiUrl',
    apiEndPoint: obj.type === 'cnoteList' ? 'barcodesPrint' : 'cnBarcodesPrint',
    method: 'GET',
    headersData,
    pathVariables:
      obj.type === 'cnoteList'
        ? undefined
        : [{ key: '{requestId}', val: obj.requestString }],
    replaceParamsArr:
      obj.type === 'cnoteList'
        ? [
            {
              key: 'printerVersion',
              val: checkForDominoPrintechClient(userProfile) ? 'DOMINO' : null
            },
            { key: 'cnotes', val: obj.requestString }
          ]
        : [
            {
              key: 'printerVersion',
              val: checkForDominoPrintechClient(userProfile) ? 'DOMINO' : null
            }
          ],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(
          dispatchPayload({
            isLoading: false,
            isLoaded: true
          })
        );
        cb(res.payload || {});
      } else {
        dispatch(dispatchPayload(defaultVal));
        toastr.error(
          'Something went wrong!',
          res.errorMessage || 'Failed to print the barcodes'
        );
      }
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error(
        'Something went wrong!',
        res.errorMessage || 'Failed to print the barcodes'
      );
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Something went wrong!', 'Failed to print the barcodes');
    }
  };

  return makeApiCall(requestObj);
};

/**
 * getUserType: To get various user types.
 *
 * @param obj
 * @param cb (callback function) - To be called after getting api response successfully
 * @returns {function(Function): null|Promise<void>}
 */
export const getUserType = (obj, cb) => (dispatch: Function) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };

  const defaultErrorMessage = 'Failed to get User Type';

  dispatch(
    dispatchUserDetails({
      isLoading: true,
      isLoaded: false
    })
  );

  toastr.light('Please Wait!', 'Fetching...', { timeOut: 0 });

  const requestObj = {
    headersData,
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'getUserType',
    method: 'GET',
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.removeByType('light');
        dispatch(
          dispatchUserDetails({
            isLoading: false,
            isLoaded: true,
            userType: res.payload
          })
        );
        cb();
      } else {
        dispatch(dispatchUserDetails(defaultVal));
        toastr.removeByType('light');
        toastr.error(
          'Something went wrong!',
          res.errorMessage || defaultErrorMessage
        );
      }
    },
    failureCb: res => {
      dispatch(dispatchUserDetails(defaultVal));
      toastr.removeByType('light');
      toastr.error(
        'Something went wrong!',
        res.errorMessage || defaultErrorMessage
      );
    },
    catchCb: () => {
      dispatch(dispatchUserDetails(defaultVal));
      toastr.removeByType('light');
      toastr.error('Something went wrong!', defaultErrorMessage);
    }
  };

  return makeApiCall(requestObj);
};

/**
 * getOrganisationDetailsList: To get organisation details list
 *
 * @returns {function(Function): null|Promise<void>}
 */
export const getOrganisationDetailsList = (cb = null) => (
  dispatch: Function
) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };

  const defaultErrorMessage = 'Failed to get Organisation List';

  dispatch(
    dispatchOrganisationDetailsList({
      isLoading: true,
      isLoaded: false
    })
  );
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'getOrganisationDetailsList',
    method: 'GET',
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(
          dispatchOrganisationDetailsList({
            isLoading: false,
            isLoaded: true,
            organisationDetailsList: res.payload
          })
        );
        if (cb) {
          cb(res.payload);
        }
      } else {
        dispatch(dispatchOrganisationDetailsList(defaultVal));
        toastr.error(
          'Something went wrong!',
          res.errorMessage || defaultErrorMessage
        );
        if (cb) {
          cb(res.payload);
        }
      }
    },
    failureCb: res => {
      dispatch(dispatchOrganisationDetailsList(defaultVal));
      toastr.error(
        'Something went wrong!',
        res.errorMessage || defaultErrorMessage
      );
    },
    catchCb: () => {
      dispatch(dispatchOrganisationDetailsList(defaultVal));
      toastr.error('Something went wrong!', defaultErrorMessage);
    }
  };

  return makeApiCall(requestObj);
};

export const bulkcnFileConverterUpload = (
  //selectedOrganisation,
  selectedClient,
  file,
  callback
) => dispatch => {
  const formData = new FormData();
  formData.append('file', file);

  const replaceParamsArr = [
    //    { key: 'organisationCode', val: selectedOrganisation.code },
    { key: 'clientCode', val: selectedClient && selectedClient.clientCode }
  ];

  dispatch(
    dispatchPayload({
      isLoading: true,
      isLoaded: false
    })
  );

  const requestObj = {
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'bulkCnConverter',
    replaceParamsArr,
    headersData: { appUuid: headersData.appUuid },
    postFormData: true,
    data: formData,
    contentType: undefined,
    method: 'POST',
    responseType: 'arraybuffer',
    fetchFileNameFromContentDisposition: true,
    notShowPopupOnError: true,
    successCb: res => {
      if (callback) {
        callback();
      }
      dispatch(
        dispatchPayload({
          isLoading: false,
          isLoaded: true
        })
      );
      if (res.status === 'FAILURE') {
        toastr.error(res.errorMessage || 'File upload failed');
        return;
      }
      toastr.success('Success', `Successfully converted file.`);
      downloadArrayBufferAsXLS(res, requestObj.fileName, true);
    },
    failureCb: res => {
      if (callback) {
        callback();
      }
      dispatch(
        dispatchPayload({
          isLoading: false,
          isLoaded: true
        })
      );
      toastr.error(
        'Something went wrong!',
        res.errorMessage || 'Failed to upload file'
      );
    },
    catchCb: res => {
      if (callback) {
        callback();
      }
      dispatch(
        dispatchPayload({
          isLoading: false,
          isLoaded: true
        })
      );
      toastr.error('Something went wrong!', 'Failed to upload file');
    }
  };

  return makeApiCall(requestObj);
};

export const getClients = (userProfile: Object) => (dispatch: Function) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };
  dispatch(
    dispatchPayload({
      isLoading: true,
      isLoaded: false
    })
  );
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: checkForClientUser(userProfile)
      ? 'getClientByUserMis'
      : 'getClientsMis',
    method: 'GET',
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(
          dispatchPayload({
            isLoading: false,
            isLoaded: true,
            clientList: res.payload || []
          })
        );
      } else {
        dispatch(dispatchPayload(defaultVal));
        toastr.error(
          'Something went wrong!',
          res.errorMessage || 'Failed to get clients'
        );
      }
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error(
        'Something went wrong!',
        res.errorMessage || 'Failed to get clients'
      );
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Something went wrong!', 'Failed to get clients');
    }
  };

  return makeApiCall(requestObj);
};

export const getTemplates = (
  clientCode: String,
  isCnCompletionCall: Boolean,
  cb: Function
) => (dispatch: Function) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };
  const requestObj = {
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'getAvailableTemplates',
    method: 'GET',
    headersData,
    replaceParamsArr: [
      { key: 'clientCode', val: clientCode },
      { key: 'isCnCompletionCall', val: isCnCompletionCall }
    ],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(
          dispatchPayload({
            isLoading: false,
            isLoaded: true
          })
        );
        cb(res.payload);
      } else {
        dispatch(dispatchPayload(defaultVal));
        cb([]);
      }
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error(
        'Something went wrong!',
        res.errorMessage || 'Failed to get templates'
      );
      cb([]);
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Something went wrong!', 'Failed to get templates');
      cb([]);
    }
  };

  return makeApiCall(requestObj);
};

/**
 * getOrganisationCodeForBfUser: To get Organisation Code For BF user
 *
 * @param cb - To be called after getting api response successfully
 * @returns {function(Function): null|Promise<void>}
 */
export const getOrganisationCodeForBfUser = cb => (dispatch: Function) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };

  const defaultErrorMessage = 'Failed to get Organisation Code';

  dispatch(
    dispatchOrganisationCodeForBfUser({
      isLoading: true,
      isLoaded: false
    })
  );
  const requestObj = {
    headersData,
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'getOrganisationCodeForBfUser',
    method: 'GET',
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(
          dispatchOrganisationCodeForBfUser({
            isLoading: false,
            isLoaded: true
          })
        );
        cb(res.payload);
      } else {
        dispatch(dispatchOrganisationCodeForBfUser(defaultVal));
        toastr.error(
          'Something went wrong!',
          res.errorMessage || defaultErrorMessage
        );
      }
    },
    failureCb: res => {
      dispatch(dispatchOrganisationCodeForBfUser(defaultVal));
      toastr.error(
        'Something went wrong!',
        res.errorMessage || defaultErrorMessage
      );
    },
    catchCb: () => {
      dispatch(dispatchOrganisationCodeForBfUser(defaultVal));
      toastr.error('Something went wrong!', defaultErrorMessage);
    }
  };

  return makeApiCall(requestObj);
};

/**
 * getTemplatesUsingOrganisationCode: To get Templates using organisation code
 *
 * @param organisationCode
 * @param cb
 * @returns {function(Function): null|Promise<void>}
 */
export const getTemplatesUsingOrganisationCode = (
  organisationCode: string,
  cb: Function
) => (dispatch: Function) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };
  const requestObj = {
    headersData,
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'getTemplateListUsingOrganisationCode',
    method: 'GET',
    replaceParamsArr: [{ key: 'organisationCode', val: organisationCode }],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(
          dispatchTemplateListUsingOrganisationCode({
            isLoading: false,
            isLoaded: true
          })
        );
        cb(res.payload || []);
      } else {
        dispatch(dispatchTemplateListUsingOrganisationCode(defaultVal));
        cb([]);
      }
    },
    failureCb: res => {
      dispatch(dispatchTemplateListUsingOrganisationCode(defaultVal));
      toastr.error(
        'Something went wrong!',
        res.errorMessage || 'Failed to get templates'
      );
      cb([]);
    },
    catchCb: () => {
      dispatch(dispatchTemplateListUsingOrganisationCode(defaultVal));
      toastr.error('Something went wrong!', 'Failed to get templates');
      cb([]);
    }
  };

  return makeApiCall(requestObj);
};

/**
 * getLocationListUsingOrganisationId: Function to get location details list using organisation id
 *
 * @param {*} organisationId
 * @param {*} cb
 * @returns
 */
export const getLocationListUsingOrganisationId = (
  organisationId,
  cb
) => dispatch => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };
  const requestObj = {
    headersData,
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'getLocationsUsingOrganisationId',
    method: 'GET',
    replaceParamsArr: [{ key: 'orgId', val: organisationId }],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(
          dispatcLocationListUsingOrganisationId({
            isLoading: false,
            isLoaded: true
          })
        );
        cb(res.payload || []);
      } else {
        dispatch(dispatcLocationListUsingOrganisationId(defaultVal));
        cb([]);
      }
    },
    failureCb: res => {
      dispatch(dispatcLocationListUsingOrganisationId(defaultVal));
      toastr.error(
        'Something went wrong!',
        res.errorMessage || 'Failed to get locations'
      );
      cb([]);
    },
    catchCb: () => {
      dispatch(dispatcLocationListUsingOrganisationId(defaultVal));
      toastr.error('Something went wrong!', 'Failed to get locations');
      cb([]);
    }
  };

  return makeApiCall(requestObj);
};

export const downloadTemplate = (templateId: string, templateName: string) => (
  dispatch: Function
) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };

  const requestObj = {
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'downloadTemplate',
    method: 'GET',
    headersData,
    pathVariables: [{ key: '{templateId}', val: templateId }],
    contentType: 'application/xml',
    responseType: 'arraybuffer',
    successCb: res => {
      const reportName = `${templateName}`;
      downloadArrayBufferAsXLS(res, reportName);
      dispatch(
        dispatchPayload({
          isLoading: false,
          isLoaded: true
        })
      );
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Error', 'Failed to download template');
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Something went wrong!', 'Failed to download template');
    }
  };

  return makeApiCall(requestObj);
};

export const getRequestSummary = (
  clientCode: String,
  isCnCompletionCall: Boolean
) => (dispatch: Function) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true,
    showTableLoader: false,
    dashBoardData: []
  };
  dispatch(
    dispatchPayload({
      showTableLoader: true
    })
  );
  const requestObj = {
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'requestSummary',
    method: 'GET',
    headersData,
    replaceParamsArr: [
      { key: 'clientCode', val: clientCode },
      { key: 'isCnCompletionCall', val: isCnCompletionCall }
    ],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(
          dispatchPayload({
            isLoading: false,
            isLoaded: true,
            dashBoardData: res.payload,
            showTableLoader: false
          })
        );
      } else {
        dispatch(dispatchPayload(defaultVal));
        toastr.error(
          'Something went wrong!',
          res.errorMessage || 'Failed to get Request Summary'
        );
      }
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error(
        'Something went wrong!',
        res.errorMessage || 'Failed to get Request Summary'
      );
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Something went wrong!', 'Failed to get Request Summary');
    }
  };

  return makeApiCall(requestObj);
};

export const getPreBookCnsTabledata = (
  clientCode,
  startDate,
  endDate
) => dispatch => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true,
    showTableLoader: false,
    dashBoardData: []
  };
  dispatch(
    dispatchPayload({
      showTableLoader: true
    })
  );
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'getPreBooksCnsData',
    method: 'GET',
    headersData,
    replaceParamsArr: [
      { key: 'clientCode', val: clientCode },
      { key: 'startDate', val: startDate },
      { key: 'endDate', val: endDate }
    ],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(
          dispatchPayload({
            isLoading: false,
            isLoaded: true,
            dashBoardData: res.payload,
            showTableLoader: false
          })
        );
      } else {
        dispatch(dispatchPayload(defaultVal));
        toastr.error(
          'Something went wrong!',
          res.errorMessage || 'Failed to get Data'
        );
      }
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error(
        'Something went wrong!',
        res.errorMessage || 'Failed to get Data'
      );
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Something went wrong!', 'Failed to get Data');
    }
  };

  return makeApiCall(requestObj);
};

export const submitPreBookCns = (
  datatToSubmit,
  callback,
  csvHeaders
) => dispatch => {
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'submitPreBookCnsData',
    method: 'POST',
    headersData,
    replaceParamsArr: [
      { key: 'clientCode', val: datatToSubmit.clientCode },
      { key: 'cnotes', val: datatToSubmit.cnotes },
      {
        key: 'noOfPreBookedCnotesRequired',
        val: datatToSubmit.noOfPreBookedCnotesRequired
      }
    ],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        jsonToCSVConvertor(res.payload || [], 'PRE BOOK CNS', csvHeaders, true);
        toastr.success('Success', `Successfully Submitted.`);
        if (callback) {
          callback();
        }
      } else {
        toastr.error(
          'Something went wrong!',
          res.errorMessage || 'Failed to Submit'
        );
      }
    },
    failureCb: res => {
      toastr.error(
        'Something went wrong!',
        res.errorMessage || 'Failed to Submit'
      );
    },
    catchCb: res => {
      toastr.error('Something went wrong!', 'Failed to Submit');
    }
  };

  makeApiCall(requestObj);
};

/**
 * getRequestSummaryUsingOrganisationCode: Function to get request summary from backend based on organisationCode.
 *
 * @param {*} organisationCode
 * @returns
 */
export const getRequestSummaryUsingOrganisationCode = organisationCode => dispatch => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true,
    showTableLoader: false,
    dashBoardData: []
  };
  dispatch(
    dispatchPayload({
      showTableLoader: true
    })
  );
  const requestObj = {
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'requestSummaryUsingOrganisationCode',
    method: 'GET',
    headersData,
    replaceParamsArr: [{ key: 'organisationCode', val: organisationCode }],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(
          dispatchPayload({
            isLoading: false,
            isLoaded: true,
            dashBoardData: res.payload,
            showTableLoader: false
          })
        );
      } else {
        dispatch(dispatchPayload(defaultVal));
        toastr.error(
          'Something went wrong!',
          res.errorMessage || 'Failed to get Request Summary'
        );
      }
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error(
        'Something went wrong!',
        res.errorMessage || 'Failed to get Request Summary'
      );
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Something went wrong!', 'Failed to get Request Summary');
    }
  };

  return makeApiCall(requestObj);
};

export const downloadRequestFile = rowData => (dispatch: Function) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };

  const requestObj = {
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'downloadRequestFile',
    method: 'GET',
    headersData,
    pathVariables: [{ key: '{requestId}', val: rowData.requestId }],
    contentType: 'application/xml',
    responseType: 'arraybuffer',
    notShowPopupOnError: true,
    fetchFileNameFromContentDisposition: true,
    successCb: res => {
      if (res && res.status && res.status === 'FAILURE') {
        toastr.error('Something went wrong!', 'Failed to download template');
        return;
      }
      const reportName = requestObj.fileName;
      downloadArrayBufferAsXLS(res, reportName);
      dispatch(
        dispatchPayload({
          isLoading: false,
          isLoaded: true
        })
      );
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Error', 'Failed to download template');
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Something went wrong!', 'Failed to download template');
    }
  };

  return makeApiCall(requestObj);
};

export const downloadResponseFile = (requestId: String) => (
  dispatch: Function
) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };

  const requestObj = {
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'downloadResponseFile',
    method: 'GET',
    headersData,
    pathVariables: [{ key: '{requestId}', val: requestId }],
    contentType: 'application/xml',
    responseType: 'arraybuffer',
    successCb: res => {
      const reportName = `Response_File_${requestId}`;
      downloadArrayBufferAsXLS(res, reportName);
      dispatch(
        dispatchPayload({
          isLoading: false,
          isLoaded: true
        })
      );
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Error', 'Failed to download template');
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Something went wrong!', 'Failed to download template');
    }
  };

  return makeApiCall(requestObj);
};

export const downloadCnCopies = (requestId: String) => (dispatch: Function) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };

  const requestObj = {
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'downloadCnCopies',
    method: 'GET',
    headersData,
    pathVariables: [{ key: '{requestId}', val: requestId }],
    contentType: 'application/pdf',
    responseType: 'blob',
    successCb: response => {
      let isPdf = null;
      for (let pair of response.headers.entries()) {
        if (
          pair[0] === 'content-type' &&
          pair[1] === 'application/json;charset=UTF-8'
        ) {
          isPdf = false;
        } else if (
          pair[0] === 'content-type' &&
          pair[1] === 'application/pdf'
        ) {
          isPdf = true;
        }
      }
      if (isPdf) {
        Promise.resolve(
          response.arrayBuffer().then(res => {
            const reportName = `Response_File_${requestId}`;
            downloadPdf(res, reportName);
          })
        );
      } else {
        Promise.resolve(
          response.json().then(res => {
            toastr.error(
              'Error',
              res && res.errorMessage
                ? res.errorMessage
                : 'Failed to download CN copies'
            );
          })
        );
      }
      dispatch(
        dispatchPayload({
          isLoading: false,
          isLoaded: true
        })
      );
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Error', 'Failed to download CN copies');
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Something went wrong!', 'Failed to download CN copies');
    }
  };

  return makeApiCall(requestObj);
};

export const retryFailedCns = (requestId: String, cb: Function) => (
  dispatch: Function
) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };

  const requestObj = {
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'retryFailedCns',
    method: 'PUT',
    headersData,
    pathVariables: [{ key: '{requestId}', val: requestId }],
    successCb: res => {
      cb();
      dispatch(
        dispatchPayload({
          isLoading: false,
          isLoaded: true
        })
      );
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Error', 'Failed to trigger retry');
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error('Something went wrong!', 'Failed to trigger retry');
    }
  };

  return makeApiCall(requestObj);
};

export const createPartialDummyPrs = (requestId: String, cb: Function) => (
  dispatch: Function
) => {
  const defaultVal = {
    isLoading: false,
    isLoaded: true
  };

  const requestObj = {
    baseUrl: 'clientIntegrationApiUrl',
    apiEndPoint: 'createPartialDummyPrs',
    method: 'POST',
    headersData,
    pathVariables: [{ key: '{requestId}', val: requestId }],
    notShowPopupOnError: true,
    successCb: res => {
      if (res.payload) {
        toastr.error('Success', 'Successfully created');
      }
      if (!res.payload) {
        toastr.error(
          'Error',
          res.errorMessage ||
            'Unable to create PRS. Check if PRS has already been created.'
        );
        return;
      }
      cb();
      dispatch(
        dispatchPayload({
          isLoading: false,
          isLoaded: true
        })
      );
    },
    failureCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error(
        'Error',
        'Unable to create PRS. Check if PRS has already been created.'
      );
    },
    catchCb: res => {
      dispatch(dispatchPayload(defaultVal));
      toastr.error(
        'Something went wrong!',
        'Unable to create PRS. Check if PRS has already been created.'
      );
    }
  };

  return makeApiCall(requestObj);
};
